import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import QueryHandler from '@aurora/shared-client/components/common/QueryHandler/QueryHandler';
import useCanSendPrivateMessages from '@aurora/shared-client/components/community/useCanSendPrivateMessages';
import AppContext from '@aurora/shared-client/components/context/AppContext/AppContext';
import useDateTime from '@aurora/shared-client/components/useDateTime';
import useQueryWithTracing from '@aurora/shared-client/components/useQueryWithTracing';
import UserAvatar from '@aurora/shared-client/components/users/UserAvatar/UserAvatar';
import UserRank from '@aurora/shared-client/components/users/UserRank/UserRank';
import useUserManagementProperties from '@aurora/shared-client/components/users/useUserManagementProperties';
import type { UserBadgesPageAndParams } from '@aurora/shared-client/routes/endUserRoutes';
import useEndUserRoutes from '@aurora/shared-client/routes/useEndUserRoutes';
import type { User } from '@aurora/shared-generated/types/graphql-schema-types';
import { EndUserComponent, EndUserPages } from '@aurora/shared-types/pages/enums';
import dynamic from 'next/dynamic';
import * as React from 'react';
import { useContext, useEffect } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import { UserKudosType } from '../../../types/enums';
import type {
  FeaturedBadgesQuery,
  FeaturedBadgesQueryVariables,
  ProfileUserQuery,
  ProfileUserQueryVariables
} from '../../../types/graphql-types';
import featuredBadgesQuery from '../../badges/FeaturedBadgesList/FeaturedBadges.query.graphql';
import FeaturedBadgesList from '../../badges/FeaturedBadgesList/FeaturedBadgesList';
import useBadgeProperties from '../../badges/useBadgeProperties';
import BanMessageBanner from '../../bans/BanMessageBanner/BanMessagebanner';
import type { WidgetFC, WidgetProps } from '../../common/Widget/types';
import useTranslation from '../../useTranslation';
import UserKudos from '../UserKudos/UserKudos';
import UserMessagesCount from '../UserMessagesCount/UserMessagesCount';
import UserSolutionCount from '../UserSolutionCount/UserSolutionCount';
import localStyles from './UserProfileWidget.module.pcss';
import userQuery from './UserProfileWidget.query.graphql';

const PrivateConversationCreationModal = dynamic(
  () => import('../../inbox/PrivateConversationCreationModal/PrivateConversationCreationModal'),
  { ssr: false }
);

/**
 * Display a user's profile.
 *
 * @constructor
 *
 * @author Dolan Halbrook, Adam Ayres, Willi Hyde, Gaston Manzano
 */
const UserProfileWidget: WidgetFC<WidgetProps> = (props): JSX.Element => {
  const { isVisible } = props;
  const [showPrivateMessageModal, setShowPrivateMessageModal] = React.useState<boolean>(false);
  const cx = useClassNameMapper(localStyles);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.USER_PROFILE_WIDGET
  );
  const { formatAbsoluteDateTime, getUserPreferredDateFormat } = useDateTime();
  const {
    contextUser,
    authUser,
    authUser: {
      profileSettings: { dateDisplayStyle, dateDisplayFormat }
    }
  } = useContext(AppContext);
  const { Link } = useEndUserRoutes();
  const { badgesEnabled, badgePropertiesLoading } = useBadgeProperties(module);
  // fall back to principal user to support page editor case
  const userId = contextUser?.id ?? authUser?.id;
  const queryResult = useQueryWithTracing<ProfileUserQuery, ProfileUserQueryVariables>(
    module,
    userQuery,
    {
      variables: {
        id: userId
      }
    }
  );

  const { data: userManagementData, loading: userManagementDataLoading } =
    useUserManagementProperties();
  const { data: userData, loading } = queryResult;
  const featuredBadgesQueryResult = useQueryWithTracing<
    FeaturedBadgesQuery,
    FeaturedBadgesQueryVariables
  >(module, featuredBadgesQuery, {
    variables: {
      userId
    },
    skip: !badgesEnabled
  });

  const { canSendPrivateMessages } = useCanSendPrivateMessages();

  useEffect(() => {
    if (!loading) {
      isVisible(!!userData?.user);
    }
  }, [userData, isVisible, loading]);

  if (textLoading || userManagementDataLoading || badgePropertiesLoading) {
    return null;
  }

  const canManageUsers =
    userManagementData?.community?.userManagementProperties?.canManageUsers?.failureReason === null;

  /**
   * Renders a link to re-direct the user to the User Badges page
   */
  function renderViewAllBadgesLink(): React.ReactElement {
    return (
      <Link<UserBadgesPageAndParams>
        legacyBehavior={true}
        route={EndUserPages.UserBadgesPage}
        params={{ login: contextUser.login, userId: contextUser.uid.toString() }}
        passHref
      >
        <Button
          as="a"
          variant={ButtonVariant.UNSTYLED}
          className={cx('lia-badge-btn')}
          data-testid="UserProfileWidget.ViewAllBadgesLink"
        >
          {formatMessage('viewAllBadges')}
        </Button>
      </Link>
    );
  }

  return (
    <QueryHandler queryResult={queryResult}>
      {(): React.ReactNode => {
        const { user } = queryResult?.data;
        const {
          login,
          registrationData: { registrationTime }
        } = user;
        const { messagesCount, solutionsCount, kudosReceivedCount } = user;
        const showUserMessagesCount = messagesCount > 0;
        const showSolutionsCount = solutionsCount > 0;
        const showKudosReceivedCount = kudosReceivedCount > 0;
        const showBadges =
          featuredBadgesQueryResult.data &&
          featuredBadgesQueryResult.data.featuredBadges.length > 0;

        return (
          <>
            {user?.banned && <BanMessageBanner />}
            <article className={cx('lia-profile')} data-testid="UserProfileWidget">
              <section className={cx('lia-profile-image')}>
                <UserAvatar user={user} size={IconSize.PX_120} />
              </section>
              <section
                data-testid={'UserProfileWidget.ProfileInfo'}
                className={cx('lia-profile-info')}
              >
                <h1 className={cx('lia-user-login')}>{login}</h1>
                <UserRank className={cx('lia-user-rank')} user={user} />
                {canManageUsers && (
                  <p className={cx('lia-user-item lia-user-email')}>{user.email}</p>
                )}
                <p
                  data-testid={'UserProfileWidget.RegistrationTime'}
                  className={cx('lia-user-item lia-user-time')}
                  title={formatAbsoluteDateTime(registrationTime)}
                >
                  {formatMessage('registrationTime', {
                    registrationTime: getUserPreferredDateFormat(
                      registrationTime,
                      dateDisplayFormat.value,
                      dateDisplayStyle.value
                    )
                  })}
                </p>
                {(showUserMessagesCount || showKudosReceivedCount || showSolutionsCount) && (
                  <section className={cx('lia-user-item lia-user-meta')}>
                    {showUserMessagesCount && <UserMessagesCount user={user} as="span" />}
                    {showKudosReceivedCount && (
                      <UserKudos
                        user={user}
                        as="span"
                        userKudosType={UserKudosType.RECEIVED}
                        useText={false}
                        useTable
                        countClassName={cx('lia-user-item')}
                        textClassName={cx('lia-user-item lia-kudos-text')}
                      />
                    )}
                    {showSolutionsCount && <UserSolutionCount user={user} as="span" />}
                  </section>
                )}
                {user.biography && (
                  <p className={cx('lia-user-item lia-user-bio')}>{user.biography}</p>
                )}
              </section>
              {showBadges && (
                <section
                  className={cx('lia-profile-badges')}
                  data-testid={'UserProfileWidget.BadgeList'}
                >
                  <FeaturedBadgesList
                    badges={featuredBadgesQueryResult.data.featuredBadges}
                    className={cx('lia-badge-list')}
                  />
                  {renderViewAllBadgesLink()}
                </section>
              )}
              {contextUser.id !== authUser.id && canSendPrivateMessages && (
                <section className={cx('lia-profile-button')}>
                  <Button
                    data-testid={'UserProfileWidget.SendMessage'}
                    className={cx('lia-send-btn')}
                    variant={ButtonVariant.SECONDARY}
                    size="lg"
                    onClick={() => setShowPrivateMessageModal(true)}
                  >
                    {formatMessage('sendMessage')}
                  </Button>
                </section>
              )}
              {showPrivateMessageModal && (
                <PrivateConversationCreationModal
                  show={showPrivateMessageModal}
                  onHide={() => setShowPrivateMessageModal(false)}
                  isSendBroadcast={false}
                  recipientUsers={[
                    {
                      label: user.login,
                      value: user.login,
                      entity: user as User
                    }
                  ]}
                />
              )}
            </article>
          </>
        );
      }}
    </QueryHandler>
  );
};

export default UserProfileWidget;
